import React, { useEffect, useState, useRef, useContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Card } from './ui/card';
import { ImagePlus, Check, X, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PostActivityPanel
 * -----------------
 * Renders after an activity ends. Two quiet modules:
 *   - Attendance correction (steward only) — per-participant toggle for "Did not show"
 *   - Photos (participants upload; steward approves)
 *
 * Design rules:
 *   - minimal, neutral colours
 *   - no emphasis, no social signals
 *   - steward controls are small inline toggles, not buttons of prominence
 */
export function PostActivityPanel({ activityId, hasEnded, isHost, isParticipant }) {
  if (!hasEnded) return null;
  return (
    <div className="space-y-6" data-testid="post-activity-panel">
      <PhotosPanel activityId={activityId} isHost={isHost} isParticipant={isParticipant} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Photos
// ---------------------------------------------------------------------------

const MAX_PHOTOS = 10;
const MAX_BYTES = 6 * 1024 * 1024;

function PhotosPanel({ activityId, isHost, isParticipant }) {
  const { user } = useContext(AuthContext);
  const [data, setData] = useState({ approved: [], pending: [], my_pending: [], is_steward: false });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef(null);

  const authHeaders = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/activities/${activityId}/photos`, {
        headers: authHeaders(), withCredentials: true,
      });
      setData(res.data || {});
    } catch {
      setData({ approved: [], pending: [], my_pending: [], is_steward: false });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activityId]);

  const readAsDataUrl = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(f => f && f.type && f.type.startsWith('image/'));
    if (files.length === 0) return;
    if (files.length > MAX_PHOTOS) {
      toast.error(`Max ${MAX_PHOTOS} photos at a time`);
      return;
    }
    for (const f of files) {
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name} is over ${MAX_BYTES / (1024 * 1024)}MB`);
        return;
      }
    }
    setUploading(true);
    try {
      const dataUrls = await Promise.all(files.map(readAsDataUrl));
      await axios.post(
        `${API}/api/activities/${activityId}/photos`,
        { photos: dataUrls },
        { headers: authHeaders(), withCredentials: true },
      );
      toast.success(`${files.length} photo${files.length === 1 ? '' : 's'} added · waiting for host approval`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const approve = async (photo_id) => {
    try {
      await axios.post(
        `${API}/api/activities/${activityId}/photos/${photo_id}/approve`,
        {}, { headers: authHeaders(), withCredentials: true },
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not approve');
    }
  };

  const reject = async (photo_id) => {
    try {
      await axios.post(
        `${API}/api/activities/${activityId}/photos/${photo_id}/reject`,
        {}, { headers: authHeaders(), withCredentials: true },
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not reject');
    }
  };

  const remove = async (photo_id) => {
    try {
      await axios.delete(
        `${API}/api/activities/${activityId}/photos/${photo_id}`,
        { headers: authHeaders(), withCredentials: true },
      );
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not remove');
    }
  };

  const showAddButton = isParticipant || isHost;

  if (loading) return null;

  return (
    <Card
      className="p-4"
      style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}
      data-testid="photos-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold" style={{ color: 'var(--fy-text-primary)' }}>
          Photos
        </h3>
        {showAddButton && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="photos-file-input"
            />
            <button
              type="button"
              onClick={() => fileRef.current && fileRef.current.click()}
              disabled={uploading}
              className="text-xs flex items-center gap-1 px-2.5 py-1 rounded-full hover:opacity-80"
              style={{ color: 'var(--lk-sage)', border: '1px solid var(--fy-border-subtle)' }}
              data-testid="add-photos-btn"
            >
              <ImagePlus className="w-3.5 h-3.5" />
              {uploading ? 'Adding…' : 'Add photos'}
            </button>
          </>
        )}
      </div>

      {/* Approved grid */}
      {data.approved && data.approved.length > 0 ? (
        <div className="grid grid-cols-3 gap-1.5" data-testid="photos-grid">
          {data.approved.map(p => (
            <figure
              key={p.photo_id}
              className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group"
              style={{ backgroundColor: 'var(--fy-surface-muted)' }}
              onClick={() => setLightbox(p)}
              data-testid={`photo-${p.photo_id}`}
            >
              <img src={p.url} alt="" loading="lazy" className="w-full h-full object-cover" />
              {p.uploaded_by_name && (
                <figcaption className="absolute inset-x-0 bottom-0 text-[10px] text-white px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent)' }}>
                  by {p.uploaded_by_name}
                </figcaption>
              )}
              {(p.uploaded_by === user?.user_id || data.is_steward) && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); remove(p.photo_id); }}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ backgroundColor: 'rgba(0,0,0,0.5)', color: '#fff' }}
                  data-testid={`photo-remove-${p.photo_id}`}
                  aria-label="Remove photo"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </figure>
          ))}
        </div>
      ) : (
        <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }} data-testid="photos-empty">
          No photos yet.{showAddButton ? ' Add a few if you were there.' : ''}
        </p>
      )}

      {/* Steward: pending queue */}
      {data.is_steward && data.pending && data.pending.length > 0 && (
        <div className="mt-4" data-testid="pending-photos-panel">
          <p className="text-xs font-semibold mb-2" style={{ color: 'var(--fy-text-secondary)' }}>
            Pending review ({data.pending.length})
          </p>
          <div className="grid grid-cols-3 gap-1.5">
            {data.pending.map(p => (
              <div
                key={p.photo_id}
                className="relative aspect-square rounded-lg overflow-hidden"
                style={{ backgroundColor: 'var(--fy-surface-muted)' }}
                data-testid={`pending-photo-${p.photo_id}`}
              >
                <img src={p.url} alt="" className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 flex items-end justify-between p-1"
                  style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent 55%)' }}>
                  <button
                    type="button"
                    onClick={() => reject(p.photo_id)}
                    className="w-7 h-7 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)', color: '#fff' }}
                    data-testid={`reject-photo-${p.photo_id}`}
                    aria-label="Reject photo"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => approve(p.photo_id)}
                    className="w-7 h-7 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'var(--lk-sage)', color: '#fff' }}
                    data-testid={`approve-photo-${p.photo_id}`}
                    aria-label="Approve photo"
                  >
                    <Check className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Participant: own pending (waiting on host) */}
      {!data.is_steward && data.my_pending && data.my_pending.length > 0 && (
        <div className="mt-4" data-testid="my-pending-photos">
          <p className="text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
            {data.my_pending.length} photo{data.my_pending.length === 1 ? '' : 's'} waiting on the host.
          </p>
          <div className="grid grid-cols-6 gap-1.5 mt-2">
            {data.my_pending.map(p => (
              <div
                key={p.photo_id}
                className="relative aspect-square rounded-md overflow-hidden opacity-70"
                style={{ backgroundColor: 'var(--fy-surface-muted)' }}
              >
                <img src={p.url} alt="" className="w-full h-full object-cover" loading="lazy" />
                <button
                  type="button"
                  onClick={() => remove(p.photo_id)}
                  className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(0,0,0,0.5)', color: '#fff' }}
                  data-testid={`my-pending-remove-${p.photo_id}`}
                  aria-label="Delete your pending photo"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[9100] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
          onClick={() => setLightbox(null)}
          data-testid="photo-lightbox"
        >
          <img
            src={lightbox.url}
            alt=""
            className="max-h-[90vh] max-w-[95vw] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </Card>
  );
}
